import React, { useEffect, useState } from "react";
import { IconFileSpreadsheet, IconUpload, IconX } from "@tabler/icons-react";
import * as XLSX from "xlsx";
import { AxiosError } from "axios";

import { brandService, BulkInviteSettings } from "../../services/brand.service";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { ActionDialog, DialogNotice, DialogTextButton } from "../common/ActionDialog";
import { cn } from "@/lib/utils";

interface ApiErrorResponse {
  success: boolean;
  message: string;
  emailsWithInvitations?: string[];
}

interface ImportAffiliatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ACCEPTED = [".csv", ".xlsx", ".xls"];

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const processFileContent = async (file: File): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const emails: string[] = [];

        if (file.name.endsWith(".csv")) {
          const content = e.target?.result as string;
          const lines = content.split("\n");

          lines.forEach((line) => {
            const email = line.trim();
            if (email && validateEmail(email)) {
              emails.push(email);
            }
          });
        } else if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          }) as unknown[][];
          jsonData.forEach((row) => {
            const email = row[0] as string;
            if (email && typeof email === "string" && validateEmail(email)) {
              emails.push(email);
            }
          });
        }

        resolve(emails);
      } catch (err) {
        console.error("Error processing file:", err);
        reject(new Error("Failed to process file"));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
    } else {
      reader.readAsBinaryString(file);
    }
  });
};

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ImportAffiliatesModal({
  isOpen,
  onClose,
  onSuccess,
}: ImportAffiliatesModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avoidDuplicates, setAvoidDuplicates] = useState(true);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [settings, setSettings] = useState<BulkInviteSettings | null>(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setConsentConfirmed(false);
    setSettingsLoading(true);
    brandService
      .getBulkInviteSettings()
      .then(setSettings)
      .catch(() => setSettings(null))
      .finally(() => setSettingsLoading(false));
  }, [isOpen]);

  const pickFile = (list: FileList | null) => {
    const picked = list?.[0];
    if (!picked) return;
    const name = picked.name.toLowerCase();
    if (!ACCEPTED.some((ext) => name.endsWith(ext))) {
      setError("That file type is not supported. Use a CSV or Excel file.");
      setFile(null);
      return;
    }
    if (settings?.maxFileSizeMb) {
      const maxBytes = settings.maxFileSizeMb * 1024 * 1024;
      if (picked.size > maxBytes) {
        setError(
          `"${picked.name}" is over the ${settings.maxFileSizeMb} MB limit. Use a smaller file, or contact admin for a higher limit.`
        );
        setFile(null);
        return;
      }
    }
    setFile(picked);
    setError(null);
  };

  const handleError = (err: unknown) => {
    if (err instanceof AxiosError) {
      const errorData = err.response?.data as ApiErrorResponse;

      if (errorData?.message) {
        switch (errorData.message) {
          case "Program not found create one first":
            setError("Create a program before inviting affiliates.");
            break;
          case "Some emails already have pending invitations":
            setError("Some of these emails already have a pending invitation. Remove them from the file and try again.");
            break;
          case "Some emails are already registered":
            setError("Some of these emails are already registered as affiliates.");
            break;
          default:
            setError(errorData.message);
        }
      } else if (err.response?.status === 400) {
        setError("The file contains invalid email addresses. Check the format and try again.");
      } else if (err.code === "ERR_NETWORK") {
        setError("Network error. Check your connection and try again.");
      } else {
        setError("The file could not be processed. Try again.");
      }
    } else {
      setError("Something went wrong. Try again.");
    }
  };

  const reset = () => {
    setFile(null);
    setError(null);
    setSuccess(null);
  };

  const close = () => {
    if (isLoading) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      if (!file) {
        setError("Choose a file first.");
        return;
      }

      const emails = await processFileContent(file);

      if (emails.length === 0) {
        setError("No valid email addresses were found in the file.");
        return;
      }

      const uniqueEmails = avoidDuplicates ? [...new Set(emails)] : emails;

      if (
        settings?.maxRecipientsPerImport &&
        uniqueEmails.length > settings.maxRecipientsPerImport
      ) {
        setError(
          `Up to ${settings.maxRecipientsPerImport} affiliates per import; this file has ${uniqueEmails.length}. Split the file, or contact admin for a higher limit.`
        );
        return;
      }

      await brandService.bulkInviteAffiliates(uniqueEmails);

      setSuccess(`${uniqueEmails.length} invitations sent.`);
      onSuccess?.();

      setTimeout(() => {
        reset();
        onClose();
      }, 1800);
    } catch (err) {
      console.error("Failed to process file:", err);
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const disabled = !!settings && !settings.bulkImportEnabled;
  const limits = [
    settings?.maxRecipientsPerImport && `up to ${settings.maxRecipientsPerImport} emails`,
    settings?.maxFileSizeMb && `${settings.maxFileSizeMb} MB`,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <ActionDialog
      open={isOpen}
      onClose={close}
      title="Import affiliates"
      footer={
        settingsLoading ? undefined : disabled ? (
          <Button type="button" variant="outline" onClick={close}>
            Close
          </Button>
        ) : (
          <>
            <DialogTextButton onClick={close} disabled={isLoading}>
              Cancel
            </DialogTextButton>
            <Button
              type="submit"
              form="import-affiliates-form"
              disabled={isLoading || !file || !consentConfirmed || !!success}
            >
              {isLoading ? "Sending invitations" : "Send invitations"}
            </Button>
          </>
        )
      }
    >
      {settingsLoading ? (
        <div className="py-6 text-center text-[13px] text-muted-foreground">
          Checking your import limits
        </div>
      ) : disabled ? (
        <DialogNotice tone="warning">
          Bulk import is turned off for this account. Contact support to enable it.
        </DialogNotice>
      ) : (
        <form id="import-affiliates-form" onSubmit={handleSubmit} className="space-y-4">
          <input
            type="file"
            onChange={(e) => {
              pickFile(e.target.files);
              e.target.value = "";
            }}
            accept={ACCEPTED.join(",")}
            className="sr-only"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files);
            }}
            className={cn(
              "block cursor-pointer rounded-md border border-dashed px-4 py-6 text-center transition-colors focus-within:ring-2 focus-within:ring-ring",
              dragging ? "border-brand-600 bg-brand-600/5" : "border-border hover:border-foreground/40"
            )}
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <IconFileSpreadsheet size={20} stroke={1.75} className="shrink-0 text-muted-foreground" />
                <span className="min-w-0 text-left">
                  <span className="block truncate text-[13px] font-medium text-foreground">{file.name}</span>
                  <span className="block text-xs text-muted-foreground">{formatSize(file.size)}</span>
                </span>
                <button
                  type="button"
                  aria-label="Remove file"
                  onClick={(e) => {
                    e.preventDefault();
                    setFile(null);
                  }}
                  className="ml-2 inline-flex size-6 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                >
                  <IconX size={14} stroke={1.75} />
                </button>
              </div>
            ) : (
              <>
                <IconUpload size={20} stroke={1.75} className="mx-auto text-muted-foreground" />
                <p className="mt-2 text-[13px] text-foreground">
                  Drop a file here, or{" "}
                  <span className="font-medium underline underline-offset-4">browse</span>
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  CSV or Excel{limits ? `, ${limits}` : ""}
                </p>
              </>
            )}
          </label>

          <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
            <label htmlFor="avoid-duplicates" className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground">
              <Checkbox
                id="avoid-duplicates"
                checked={avoidDuplicates}
                onCheckedChange={(v) => setAvoidDuplicates(v === true)}
              />
              Skip duplicate emails
            </label>
            <span className="text-muted-foreground">
              Templates:{" "}
              <a
                href="/docs/templates/affiliate-list.csv"
                download
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                CSV
              </a>
              {", "}
              <a
                href="/docs/templates/affiliate-list.xlsx"
                download
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                Excel
              </a>
            </span>
          </div>

          <div className="rounded-md border border-border bg-accent/50 p-3">
            <label htmlFor="email-consent" className="flex cursor-pointer items-start gap-2 text-[13px] text-foreground">
              <Checkbox
                id="email-consent"
                checked={consentConfirmed}
                onCheckedChange={(v) => setConsentConfirmed(v === true)}
                className="mt-0.5"
              />
              I confirm that everyone on this list has agreed to receive emails from us.
            </label>
            <p className="mt-2 text-xs text-muted-foreground">
              Our email provider, Postmark, only allows sending to recipients who have
              explicitly opted in. Uploading addresses that haven&apos;t agreed leads to
              spam complaints and can get our email delivery suspended.{" "}
              <a
                href="https://postmarkapp.com/guides/best-practices-for-broadcast-sending"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-foreground underline-offset-4 hover:underline"
              >
                Read Postmark&apos;s sending guidelines
              </a>
              .
            </p>
          </div>

          {error && <DialogNotice tone="error">{error}</DialogNotice>}
          {success && <DialogNotice tone="success">{success}</DialogNotice>}
        </form>
      )}
    </ActionDialog>
  );
}
